import styles from "./DragHandle.module.css"

import IconSelectDragReorderDots from "@shared/components/icons/SelectDragReorderDots"
import { cx } from "@shared/components/untitledui/util/cx"
import type { Editor } from "@tiptap/core"
import { useCallback, type ReactNode } from "react"
import { selectNodeAsDragMechanics } from "../../extensions/drag-mechanics-selection/drag-mechanics-selection-extension"
import { startDragPreview } from "./DragPreview"

export interface DragHandleProps {
  editor: Editor
  /** Position of the node to select/drag — usually the node view's getPos. */
  getPos: () => number | undefined
  /**
   * Extra class for geometry overrides: declare --drag-handle-* CSS vars in it
   * (defaults in DragHandle.module.css).
   */
  className?: string
  /** Custom icon/content; defaults to the reorder dots. */
  children?: ReactNode
}

/**
 * A ProseMirror drag handle: selects its node on click and initiates a drag of it.
 *
 * The node's extension spec must set `draggable: true`. (This is used for ProseMirror's
 * internal drag-and-drop tracking; it's distinct from setting draggable="true" on the
 * drag handle itself.)
 *
 * Visibility: the handle starts at opacity 0. The global class
 * `draggable-node-view-drag-handle` is kept on the element so existing hover rules
 * (DraggableNodeViewWrapper.module.css, card.module.css) can
 * reveal it.
 */
export function DragHandle({ editor, getPos, className, children }: DragHandleProps) {
  /** Selects the node when you click the drag handle. */
  const handleMouseDown = useCallback(() => {
    const pos = getPos()
    if (pos === undefined) return

    selectNodeAsDragMechanics(editor, pos)
    // Don't preventDefault: it stops card drags from ever starting. Rogue-selection
    // paint is handled by the ungated ::selection rules instead (card.module.css).
  }, [editor, getPos])

  /** Replaces the native translucent drag ghost with a Trello-style card preview. */
  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    const nodeDom = e.currentTarget.closest("[data-node-view-wrapper]")
    if (nodeDom instanceof HTMLElement) {
      startDragPreview(e.nativeEvent, nodeDom)
    }
  }, [])

  return (
    <div
      className={cx("draggable-node-view-drag-handle", styles.dragHandle, className)}
      contentEditable={false}
      onMouseDown={handleMouseDown}
      // ProseMirror's workaround for some Chrome behavior causes a TextSelection when you
      // mouseup on the drag pad, overriding our NodeSelection:
      // https://github.com/ProseMirror/prosemirror-view/blob/17af0f4b25933933f644a655ad6bb9a0249d7cb5/src/input.ts#L392
      // Workaround the workaround by stopping it from receiving the mouseup event.
      // (NodeView React roots live inside the editor DOM, so React handlers here run
      // before ProseMirror's listeners — stopping propagation really does block them.)
      onMouseUp={(e) => e.stopPropagation()}
      onDragStart={handleDragStart}
      // draggable="true" ensures that this element can receive dragstart events,
      // which bubble up to the NodeView (even though the actual content is not draggable).
      // The NodeView also looks for data-drag-handle and uses it to tweak the drag image
      // (a call that startDragPreview swallows in favor of its own preview).
      // See https://github.com/ueberdosis/tiptap/blob/main/packages/core/src/NodeView.ts
      draggable="true"
      data-drag-handle
    >
      {children ?? <IconSelectDragReorderDots />}
    </div>
  )
}
